import { useEffect, useState } from 'react'
import { scheduleApi, blocksApi } from '../api'
import { Check, Minus, X, Save, ChevronDown, ChevronUp } from 'lucide-react'

// ── Constants ──────────────────────────────────────────────────────────────────

const DAY_NAMES = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche']
const DAY_SHORT = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']

const STATUS = {
  done:    { label: 'Fait',    color: 'var(--done)',    bg: 'var(--done-bg)' },
  partial: { label: 'Partiel', color: 'var(--partial)', bg: 'var(--partial-bg)' },
  skipped: { label: 'Sauté',   color: 'var(--skip)',    bg: 'var(--skip-bg)' },
  pending: { label: 'À faire', color: 'var(--muted)',   bg: 'transparent' },
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function Label({ children }) {
  return <p className="text-[11px] font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--accent)' }}>{children}</p>
}

function getTodayDayOfWeek() {
  return (new Date().getDay() + 6) % 7 // 0=Lundi, 6=Dimanche
}

// ── BlockCard — vue aujourd'hui ────────────────────────────────────────────────

function BlockCard({ block, onUpdate }) {
  const [saving,   setSaving]   = useState(false)
  const [expanded, setExpanded] = useState(false)
  const cfg = STATUS[block.status] || STATUS.pending

  const handle = async (status) => {
    setSaving(true)
    try { await onUpdate(block.block_id, block.log_id, status) }
    finally { setSaving(false) }
  }

  return (
    <div className="glass rounded-2xl overflow-hidden transition-all"
      style={{
        border: block.is_current ? '1px solid rgba(123,111,208,0.4)' : 'var(--glass-border)',
        marginBottom: '10px',
      }}>
      {/* Header */}
      <div className="flex items-start gap-3 p-4">
        <div className="flex-1 min-w-0">
          {block.is_current && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full mr-2 mb-1 inline-block"
              style={{ background: 'var(--accent)', color: 'white' }}>MAINTENANT</span>
          )}
          <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{block.name}</p>
          <p className="text-xs mt-0.5" style={{ color: 'var(--muted)' }}>
            {block.time_start?.slice(0,5)} – {block.time_end?.slice(0,5)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold px-2.5 py-1 rounded-full"
            style={{ background: cfg.bg, color: cfg.color }}>{cfg.label}</span>
          {block.description && (
            <button onClick={() => setExpanded(e => !e)} style={{ color: 'var(--muted)' }}>
              {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          )}
        </div>
      </div>

      {/* Description expandable */}
      {expanded && block.description && (
        <div className="px-4 pb-3">
          <p className="text-xs leading-relaxed" style={{ color: 'var(--text2)' }}>{block.description}</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 px-4 pb-4">
        {[
          { s: 'done',    icon: Check, label: 'Fait' },
          { s: 'partial', icon: Minus, label: 'Partiel' },
          { s: 'skipped', icon: X,     label: 'Sauté' },
        ].map(({ s, icon: Icon, label }) => (
          <button key={s} onClick={() => handle(s)} disabled={saving}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-2xl text-xs font-medium transition"
            style={{
              background: block.status === s ? STATUS[s].bg   : 'var(--bg3)',
              color:      block.status === s ? STATUS[s].color : 'var(--muted)',
              border:     block.status === s ? `1px solid ${STATUS[s].color}30` : '1px solid transparent',
            }}>
            <Icon size={12} />{label}
          </button>
        ))}
      </div>
    </div>
  )
}

// ── WeekView — vue semaine ─────────────────────────────────────────────────────

function WeekView({ weekData, onEditDay }) {
  const todayDow = getTodayDayOfWeek()
  const [selectedDay, setSelectedDay] = useState(todayDow)

  const day = weekData.find(d => d.day_of_week === selectedDay)

  return (
    <div>
      {/* Sélecteur de jour */}
      <div className="flex gap-1.5 mb-5 overflow-x-auto pb-1">
        {weekData.map(d => (
          <button key={d.day_of_week}
            onClick={() => setSelectedDay(d.day_of_week)}
            className="flex-shrink-0 flex flex-col items-center px-3 py-2 rounded-2xl transition"
            style={{
              background: selectedDay === d.day_of_week ? 'var(--accent)' : 'var(--bg2)',
              border:     d.day_of_week === todayDow ? '1px solid rgba(123,111,208,0.4)' : 'var(--glass-border)',
            }}>
            <span className="text-[10px] font-bold"
              style={{ color: selectedDay === d.day_of_week ? 'rgba(255,255,255,0.7)' : 'var(--muted)' }}>
              {DAY_SHORT[d.day_of_week]}
            </span>
            <span className="text-xs font-bold mt-0.5"
              style={{ color: selectedDay === d.day_of_week ? 'white' : 'var(--text)' }}>
              {d.blocks.length}
            </span>
          </button>
        ))}
      </div>

      {/* Blocs du jour sélectionné */}
      {day && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-base font-bold" style={{ color: 'var(--text)' }}>
              {DAY_NAMES[selectedDay]}
              {selectedDay === todayDow && (
                <span className="text-xs font-normal ml-2" style={{ color: 'var(--accent)' }}>Aujourd'hui</span>
              )}
            </p>
            <button onClick={() => onEditDay(selectedDay)}
              className="text-xs font-semibold px-3 py-1.5 rounded-full"
              style={{ background: 'var(--bg3)', color: 'var(--muted)' }}>
              Modifier
            </button>
          </div>

          <div className="space-y-2">
            {day.blocks.length === 0 ? (
              <p className="text-sm text-center py-6" style={{ color: 'var(--muted)' }}>
                Aucun bloc ce jour
              </p>
            ) : (
              day.blocks.map(b => (
                <div key={b.block_id} className="glass rounded-2xl p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{b.name}</p>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--muted)' }}>
                        {b.time_start?.slice(0,5)} – {b.time_end?.slice(0,5)}
                      </p>
                      {b.description && (
                        <p className="text-xs mt-2 leading-relaxed" style={{ color: 'var(--text2)' }}>
                          {b.description}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ── DayEditor — édition des blocs d'un jour ───────────────────────────────────

function DayEditor({ weekData, initialDay, onSaved, onBack }) {
  const [selectedDay, setSelectedDay] = useState(initialDay ?? 0)
  const [draft,       setDraft]       = useState([])
  const [saving,      setSaving]      = useState(false)
  const [saved,       setSaved]       = useState(false)

  const day = weekData.find(d => d.day_of_week === selectedDay)

  useEffect(() => {
    if (!day) return
    setDraft(day.blocks.map(b => ({
      id:          b.block_id,
      name:        b.name,
      description: b.description ?? '',
      time_start:  b.time_start?.slice(0, 5),
      time_end:    b.time_end?.slice(0, 5),
    })))
    setSaved(false)
  }, [selectedDay])

  const update = (index, field, value) => {
    setDraft(d => d.map((b, i) => i === index ? { ...b, [field]: value } : b))
    setSaved(false)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await Promise.all(draft.map(b =>
        scheduleApi.update(b.id, {
          name:        b.name,
          description: b.description,
          time_start:  b.time_start + ':00',
          time_end:    b.time_end   + ':00',
        })
      ))
      setSaved(true)
      onSaved()
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      {/* Sélecteur de jour */}
      <div className="flex gap-1.5 mb-5 overflow-x-auto pb-1">
        {weekData.map(d => (
          <button key={d.day_of_week}
            onClick={() => setSelectedDay(d.day_of_week)}
            className="flex-shrink-0 px-3 py-2 rounded-2xl text-xs font-bold transition"
            style={{
              background: selectedDay === d.day_of_week ? 'var(--accent)' : 'var(--bg2)',
              color:      selectedDay === d.day_of_week ? 'white' : 'var(--muted)',
            }}>
            {DAY_SHORT[d.day_of_week]}
          </button>
        ))}
      </div>

      <p className="text-base font-bold mb-4" style={{ color: 'var(--text)' }}>
        {DAY_NAMES[selectedDay]}
      </p>

      {/* Blocs éditables */}
      <div className="space-y-3 mb-4">
        {draft.map((b, i) => (
          <div key={b.id} className="glass rounded-2xl p-4">
            {/* Nom */}
            <div className="flex items-center gap-2 mb-3">
              <span className="w-6 h-6 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0"
                style={{ background: 'var(--accent-bg)', color: 'var(--accent)' }}>
                {i + 1}
              </span>
              <input value={b.name} onChange={e => update(i, 'name', e.target.value)}
                className="flex-1 px-3 py-1.5 rounded-xl text-sm font-semibold outline-none"
                style={{ background: 'var(--bg3)', color: 'var(--text)', border: 'var(--glass-border)' }} />
            </div>

            {/* Description */}
            <textarea value={b.description}
              onChange={e => update(i, 'description', e.target.value)}
              placeholder="Description détaillée de ce bloc…"
              rows={2}
              className="w-full px-3 py-2 rounded-xl text-xs outline-none resize-none mb-3"
              style={{ background: 'var(--bg3)', color: 'var(--text2)', border: 'var(--glass-border)' }} />

            {/* Horaires */}
            <div className="flex gap-2 items-center">
              <input type="time" value={b.time_start}
                onChange={e => update(i, 'time_start', e.target.value)}
                className="flex-1 px-3 py-1.5 rounded-xl text-sm outline-none text-center"
                style={{ background: 'var(--bg3)', color: 'var(--text)', border: 'var(--glass-border)' }} />
              <span className="text-xs" style={{ color: 'var(--muted)' }}>→</span>
              <input type="time" value={b.time_end}
                onChange={e => update(i, 'time_end', e.target.value)}
                className="flex-1 px-3 py-1.5 rounded-xl text-sm outline-none text-center"
                style={{ background: 'var(--bg3)', color: 'var(--text)', border: 'var(--glass-border)' }} />
            </div>
          </div>
        ))}
      </div>

      <button onClick={handleSave} disabled={saving}
        className="w-full py-3 rounded-2xl text-sm font-semibold flex items-center justify-center gap-2 transition"
        style={{
          background: saved ? 'var(--done-bg)' : 'var(--accent)',
          color:      saved ? 'var(--done)'    : 'white',
          opacity:    saving ? 0.7 : 1,
          boxShadow:  saved ? 'none' : '0 4px 16px rgba(123,111,208,0.3)',
        }}>
        {saving ? 'Sauvegarde…' : saved ? <><Check size={14} /> Sauvegardé</> : <><Save size={14} /> Sauvegarder {DAY_NAMES[selectedDay]}</>}
      </button>
    </div>
  )
}

// ── HistoryBar ─────────────────────────────────────────────────────────────────

function HistoryBar({ entry }) {
  const done  = Number(entry.done)
  const total = Number(entry.total)
  const pct   = total > 0 ? Math.round((done / total) * 100) : 0
  const color = pct >= 70 ? 'var(--done)' : pct >= 40 ? 'var(--partial)' : 'var(--skip)'

  return (
    <div className="flex items-center gap-3 py-1.5">
      <span className="text-xs w-24 flex-shrink-0" style={{ color: 'var(--muted)' }}>
        {new Date(entry.log_date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
      </span>
      <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--bg4)' }}>
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="text-xs w-8 text-right" style={{ color: 'var(--muted)' }}>{done}/{total}</span>
    </div>
  )
}

// ── Schedule ───────────────────────────────────────────────────────────────────

export default function Schedule() {
  const [todayBlocks, setTodayBlocks] = useState([])
  const [weekData,    setWeekData]    = useState([])
  const [history,     setHistory]     = useState([])
  const [streak,      setStreak]      = useState(0)
  const [loading,     setLoading]     = useState(true)
  const [tab,         setTab]         = useState('today')
  const [editDay,     setEditDay]     = useState(null)

  const load = async () => {
    try {
      const [today, week, hist] = await Promise.all([
        scheduleApi.today(),
        scheduleApi.week(),
        scheduleApi.history(),
      ])
      setTodayBlocks(today.blocks || [])
      setWeekData(week.days || [])
      setHistory(hist.days || [])
      setStreak(hist.streak ?? 0)
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const handleUpdate = async (blockId, logId, status) => {
    // Optimistic update
    setTodayBlocks(p => p.map(b => b.block_id === blockId ? { ...b, status } : b))

    if (!logId) {
      const fresh = await blocksApi.initDay()
      setTodayBlocks(fresh.blocks || [])
      const log = (fresh.blocks || []).find(b => b.block_id === blockId)
      if (log?.log_id) await blocksApi.setStatus(log.log_id, status)
    } else {
      await blocksApi.setStatus(logId, status)
    }
  }

  const done  = todayBlocks.filter(b => b.status === 'done').length
  const total = todayBlocks.length

  const TABS = [
    { id: 'today',   label: "Aujourd'hui" },
    { id: 'week',    label: 'Semaine'     },
    { id: 'history', label: 'Historique'  },
    { id: 'edit',    label: 'Modifier'    },
  ]

  return (
    <div className="px-4 w-full max-w-lg mx-auto" style={{ paddingTop: '24px', paddingBottom: '120px' }}>
      <h1 className="text-2xl font-bold mb-1" style={{ color: 'var(--text)' }}>Planning</h1>
      <p className="text-sm mb-4" style={{ color: 'var(--muted)' }}>
        {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
      </p>

      {/* Tabs */}
      <div className="flex gap-2 mb-5 overflow-x-auto pb-1">
        {TABS.map(t => (
          <button key={t.id} onClick={() => { setTab(t.id); setEditDay(null) }}
            className="flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-semibold transition"
            style={{
              background: tab === t.id ? 'var(--accent)' : 'var(--bg2)',
              color:      tab === t.id ? 'white' : 'var(--muted)',
              boxShadow:  tab === t.id ? '0 4px 12px rgba(123,111,208,0.25)' : 'none',
              border:     'var(--glass-border)',
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Aujourd'hui ── */}
      {tab === 'today' && (
        <>
          {/* Barre progression */}
          <div className="glass rounded-2xl p-4 mb-4 flex items-center gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold" style={{ color: 'var(--accent)' }}>{done}</p>
              <p className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Faits</p>
            </div>
            <div className="flex-1">
              <div className="h-2 rounded-full overflow-hidden mb-1" style={{ background: 'var(--bg4)' }}>
                <div className="h-full rounded-full transition-all"
                  style={{ width: `${total > 0 ? (done / total) * 100 : 0}%`, background: 'var(--accent)',
                           boxShadow: '0 0 8px rgba(123,111,208,0.5)' }} />
              </div>
              {streak > 0 && (
                <p className="text-[10px] text-center" style={{ color: 'var(--muted)' }}>🔥 {streak} jours de streak</p>
              )}
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold" style={{ color: 'var(--text)' }}>{total}</p>
              <p className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Total</p>
            </div>
          </div>

          {loading ? (
            Array(7).fill(0).map((_, i) => (
              <div key={i} className="h-24 rounded-2xl animate-pulse mb-3" style={{ background: 'var(--bg2)' }} />
            ))
          ) : todayBlocks.length === 0 ? (
            <div className="glass rounded-3xl p-8 text-center">
              <p className="text-3xl mb-3">📅</p>
              <p className="text-sm font-semibold mb-1" style={{ color: 'var(--text)' }}>Aucun bloc aujourd'hui</p>
              <p className="text-xs" style={{ color: 'var(--muted)' }}>Configure ton planning dans l'onglet Modifier</p>
            </div>
          ) : (
            todayBlocks.map(b => (
              <BlockCard key={b.block_id} block={b} onUpdate={handleUpdate} />
            ))
          )}
        </>
      )}

      {/* ── Semaine ── */}
      {tab === 'week' && !loading && (
        <WeekView
          weekData={weekData}
          onEditDay={(day) => { setEditDay(day); setTab('edit') }}
        />
      )}

      {/* ── Historique ── */}
      {tab === 'history' && (
        <div className="glass rounded-2xl p-4">
          <Label>30 derniers jours</Label>
          {history.length === 0
            ? <p className="text-sm text-center py-6" style={{ color: 'var(--muted)' }}>Pas encore d'historique</p>
            : history.map(e => <HistoryBar key={e.log_date} entry={e} />)
          }
        </div>
      )}

      {/* ── Modifier ── */}
      {tab === 'edit' && !loading && (
        <DayEditor
          weekData={weekData}
          initialDay={editDay ?? getTodayDayOfWeek()}
          onSaved={load}
          onBack={() => setTab('week')}
        />
      )}
    </div>
  )
}